import React, { useEffect, useState, useCallback } from 'react';
import { useAppContext } from 'Context/AppContext';
import { Box, IconButton } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { NoteCard } from 'Components/NoteCard';
import { Card } from 'antd';
import styles from 'Components/card.module.css';
import { Input } from 'antd';
import CloseIcon from '@mui/icons-material/Close';
import { Button } from 'antd';
import { ResponseDataType, getRequest, postRequest, putRequest, deleteRequest } from 'api';
import { toast } from 'react-toastify';

const { TextArea } = Input;

interface NoteItemData {
  id: string;
  title: string;
  content: string;
}

interface NoteListResponseDto {
  notes: NoteItemData[];
}

export default function Note() {
  const { toggleNoteApp } = useAppContext();
  const [addCardButton, setAddCardButton] = useState<boolean>(false);
  const organizationId = localStorage.getItem('organizationId');
  const customerId = localStorage.getItem('customerId');
  const [notes, setNotes] = useState<NoteItemData[]>([]);
  const [getListNotes, setGetListNotes] = useState<boolean>(false);

  const [newNote, setNewNote] = useState({
    title: '',
    content: '',
  });

  const handleNewNoteTitleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setNewNote({ ...newNote, title: e.target.value });
  };

  const handleNewNoteContentChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setNewNote({ ...newNote, content: e.target.value });
  };

  const handleSaveNewNote = useCallback(async () => {
    if (!newNote.title || !newNote.content) {
      toast.error('Tiêu đề và nội dung không được để trống');
      return;
    }
    if (!customerId || !organizationId) {
      toast.error('customerId hoặc organizationId không hợp lệ');
      return;
    }
    // console.log('Creating note with:', { title: newNote.title, content: newNote.content, organizationId }); // Debug
    const response = await postRequest('/notes/create', {
      title: newNote.title,
      content: newNote.content,
      organizationId,
    });
    // console.log('Create note response:', response); // Debug
    if (response.code) {
      toast.error(response.message);
    } else {
      setAddCardButton(false);
      setNewNote({ title: '', content: '' });
      setGetListNotes(!getListNotes);
    }
  }, [newNote, customerId, organizationId, getListNotes]);

  const getListNoteCard = useCallback(async () => {
    if (!customerId || !organizationId) {
      toast.error('customerId hoặc organizationId không hợp lệ');
      return;
    }
    // console.log('Fetching notes with:', { customerId, organizationId }); // Debug
    const response: ResponseDataType<NoteListResponseDto> = await getRequest(
      `/notes/get-notes-list?customerId=${customerId}&organizationId=${organizationId}`
    );

    // console.log('API response:', response); // Debug

    if (response.code) {
      toast.error(response.message);
      setNotes([]);
    } else if (response.data && Array.isArray(response.data.notes)) {
      const resData: NoteItemData[] = response.data.notes.map((note: any) => ({
        id: note.id as string,
        title: note.title as string,
        content: note.content as string,
      }));
      setNotes(resData);
    } else {
      console.error('Dữ liệu note không hợp lệ:', response.data);
      toast.error('Lỗi khi tải danh sách ghi chú.');
      setNotes([]);
    }
  }, [customerId, organizationId]);

  useEffect(() => {
    getListNoteCard();
  }, [getListNoteCard, getListNotes]);

  const handleDeleteNote = useCallback(async (id: string) => {
    if (!id) {
      toast.error('ID ghi chú không hợp lệ');
      return;
    }
    // console.log('Deleting note:', id); // Debug
    const response = await deleteRequest(`/notes/delete/${id}`);
    // console.log('Delete note response:', response); // Debug
    if (response.code) {
      toast.error(response.message);
    } else {
      setGetListNotes(!getListNotes);
    }
  }, [getListNotes]);

  const handleEditNote = useCallback(async (id: string, title: string, content: string) => {
    if (!id || !title || !content) {
      toast.error('ID, tiêu đề hoặc nội dung không hợp lệ');
      return;
    }
    // console.log('Updating note:', { id, title, content }); // Debug
    const response = await putRequest(`/notes/${id}`, { title, content });
    // console.log('Update note response:', response); // Debug
    if (response.code) {
      toast.error(response.message);
    } else {
      setNotes(prev =>
        prev.map(note => (note.id === id ? { ...note, title, content } : note))
      );
      setGetListNotes(!getListNotes);
    }
  }, [getListNotes]);

  return (
    <Box sx={{ height: '100%' }}>
      {/* header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          pt: 2,
          height: 50,
          borderBottom: '1px solid rgb(237, 237, 237)',
        }}
      >
        <Box
          sx={{
            color: 'black',
            fontWeight: 600,
            fontFamily: 'Quicksand',
            fontSize: 16,
            ml: 2,
          }}
        >
          Ghi chú
        </Box>
        <div onClick={toggleNoteApp}>
          <IconButton
            sx={{
              color: '#888888',
              mr: 1,
              mt: -1,
            }}
            children={<CloseIcon />}
          />
        </div>
      </Box>

      <Box
        sx={{
          overflowY: 'auto',
          height: '90%',
          '::-webkit-scrollbar': {
            display: 'none',
          },
        }}
      >
        <Box>
          {!addCardButton && (
            <Box
              sx={{
                fontSize: '14px',
                fontWeight: 700,
                color: '#555555',
                fontFamily: 'Quicksand',
                display: 'flex',
                alignItems: 'center',
                margin: 1,
                height: '40px',
                width: 280,
                borderRadius: 50,
                '&:hover': {
                  cursor: 'pointer',
                  backgroundColor: '#efefef',
                },
              }}
              onClick={() => setAddCardButton(true)}
            >
              <AddIcon sx={{ mr: 1.5, ml: 1 }} />
              Tạo ghi chú...
            </Box>
          )}

          {addCardButton && (
            <div className={styles.card_container}>
              <Card className={styles.card}>
                <div style={{ position: 'absolute', top: '2px', right: '2px' }}>
                  <IconButton
                    sx={{ color: '#888888' }}
                    onClick={() => setAddCardButton(false)}
                  >
                    <CloseIcon sx={{ fontSize: '16px' }} />
                  </IconButton>
                </div>
                <div style={{ marginBottom: '15px' }}>
                  <TextArea
                    className={styles.custom_input_title_box}
                    placeholder='Tiêu đề'
                    variant='underlined'
                    autoSize
                    value={newNote.title}
                    onChange={handleNewNoteTitleChange}
                  />
                  <TextArea
                    className={styles.custom_input_content_box}
                    placeholder='Tạo ghi chú...'
                    variant='underlined'
                    autoSize
                    value={newNote.content}
                    onChange={handleNewNoteContentChange}
                  />
                </div>

                <div
                  style={{
                    display: 'flex',
                    position: 'absolute',
                    bottom: 6,
                    right: 5,
                  }}
                >
                  <Button
                    type='text'
                    style={{ fontWeight: 600 }}
                    onClick={handleSaveNewNote}
                  >
                    Xong
                  </Button>
                </div>
              </Card>
            </div>
          )}
        </Box>

        {/* List Note_card */}
        <Box>
          {notes.length === 0 ? (
            <Box
              sx={{
                textAlign: 'center',
                color: '#888888',
                fontFamily: 'Quicksand',
                mt: 5,
              }}
            >
              Chưa có ghi chú nào!
            </Box>
          ) : (
            notes.map(note => (
              <NoteCard
                key={note.id}
                id={note.id}
                title={note.title}
                content={note.content}
                onDelete={handleDeleteNote}
                onEdit={handleEditNote}
              />
            ))
          )}
        </Box>
      </Box>
    </Box>
  );
}